import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'

import {
  PaginatedProductCatalogSearchResults as ApiPaginatedProductCatalogSearchResults,
  ProductCatalogSearchQueryVariables as ApiProductCatalogSearchQueryVariables,
  searchProductCatalog,
} from '../api'
import { keys as queryKeys } from '../queryKeys'

export type ProductCatalogSearchQueryVariables =
  ApiProductCatalogSearchQueryVariables

export interface ProductCatalogSearchResultItemFE {
  id: string
  name: string
  description: string
  categoryId: string
  ccnPath: string[]
}

export interface PaginatedProductCatalogSearchResultsFE {
  data: ProductCatalogSearchResultItemFE[]
  total: number
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  perPage: number
  totalPages: number
}

const useSearchProductCatalogQuery = (
  variables: ProductCatalogSearchQueryVariables,
  componentEnabled: boolean = true
): UseQueryResult<PaginatedProductCatalogSearchResultsFE, Error> => {
  const { accountId, catalogId, region, query, page, pageSize } = variables

  const queryIsEnabled =
    componentEnabled &&
    !!query &&
    query.length > 0 &&
    !!catalogId &&
    !!region &&
    !!accountId

  return useQuery<
    ApiPaginatedProductCatalogSearchResults,
    Error,
    PaginatedProductCatalogSearchResultsFE
  >(
    queryKeys.productCatalogSearch({
      accountId,
      catalogId,
      region,
      query,
      page,
      pageSize,
    }),
    () => searchProductCatalog(variables),
    {
      staleTime: 20,
      enabled: queryIsEnabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      select: (apiData) => ({
        ...apiData,
        data: apiData.data.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          categoryId: item.category_id,
          ccnPath: item.ccn_path,
        })),
      }),
      onError: (error: Error) => {
        errorToast(`Failed to search catalog: ${error.message}`)
      },
    }
  )
}

export default useSearchProductCatalogQuery
